import { useTheme } from "@mui/material";
import { Clock, type LucideIcon, PiggyBank } from "lucide-react";
import { useMemo } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { ExtraContributionScenario } from "web_myplann/Goals/Types/ExtraContributionScenario";
import type { CurrencyEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export type ExtraContributionScenarioInfo = {
	label: string;
	subtitle: string;
	accent: string;
	icon: LucideIcon;
};

type UseExtraContributionScenariosParams = {
	canFinishEarlier: boolean;
	savedLabel: string;
	extraTotal: number;
	currencyCode?: CurrencyEnum;
};

type UseExtraContributionScenariosResult = {
	scenarios: ExtraContributionScenario[];
	scenarioInfo: Record<
		ExtraContributionScenario,
		ExtraContributionScenarioInfo
	>;
};

const ScenarioResources = Resources.Goals.Detail.ExtraContribution;

export const useExtraContributionScenarios = ({
	canFinishEarlier,
	savedLabel,
	extraTotal,
	currencyCode,
}: UseExtraContributionScenariosParams): UseExtraContributionScenariosResult => {
	const { palette } = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	return useMemo(
		() => ({
			scenarios: canFinishEarlier
				? [ExtraContributionScenario.Earlier, ExtraContributionScenario.More]
				: [ExtraContributionScenario.More],
			scenarioInfo: {
				[ExtraContributionScenario.Earlier]: {
					label: t(ScenarioResources.Earlier.label),
					subtitle: t(ScenarioResources.Earlier.subtitle, {
						saved: savedLabel,
					}),
					accent: palette.success.main,
					icon: Clock,
				},
				[ExtraContributionScenario.More]: {
					label: t(ScenarioResources.More.label),
					subtitle: t(ScenarioResources.More.subtitle, {
						amount: formatAmount(extraTotal, currencyCode),
					}),
					accent: palette.blue.main,
					icon: PiggyBank,
				},
			},
		}),
		[
			canFinishEarlier,
			savedLabel,
			extraTotal,
			currencyCode,
			t,
			palette,
			formatAmount,
		],
	);
};
